<template>
  <aside
    id="app-sidebar"
    class="preview-sidebar"
  >
    <ul class="preview-sidebar__list">
      <AppSidebarItem
        v-for="(item, index) in items"
        :key="item.link || index"
        :item="item"
        :active="isItemActive(item)"
      />
    </ul>

    <div
      id="app-sidebar-fab"
      class="preview-sidebar__fab"
    >
      <SpeedDial
        :model="items"
        direction="up"
        :transition-delay="60"
        :hide-on-click-outside="true"
        :pt="{
          list: { class: 'preview-sidebar__fab-list' },
          item: { class: 'preview-sidebar__fab-item-wrap' }
        }"
      >
        <template #button="{ visible, toggleCallback }">
          <button
            class="preview-sidebar__fab-btn"
            :class="{ 'preview-sidebar__fab-btn--open': visible }"
            type="button"
            @click="toggleCallback"
          >
            <RriIcon
              :icon="visible ? 'nav-add' : 'nav-category'"
              :class="{ 'preview-sidebar__fab-btn-icon--open': visible }"
              size="md"
            />
          </button>
        </template>
        <template #item="{ item, onClick }">
          <div class="preview-sidebar__fab-item">
            <span class="preview-sidebar__fab-label">
              {{ item.label }}
            </span>
            <RouterLink
              :to="item.link"
              class="preview-sidebar__fab-action"
              :class="{ 'preview-sidebar__fab-action--active': isItemActive(item) }"
              @click="onClick"
            >
              <RriIcon
                :icon="item.icon"
                size="md"
              />
              <BadgeNumeric
                :value="item.badge"
                size="sm"
              />
            </RouterLink>
          </div>
        </template>
      </SpeedDial>
    </div>
  </aside>
</template>

<script>
import { useRoute } from 'vue-router'
import SpeedDial from 'primevue/speeddial'
import RriIcon from '@/components/rri/RriIcon.vue'
import AppSidebarItem from '@/v2/components/layout/AppSidebarItem.vue'
import BadgeNumeric from '@/components/common/BadgeNumeric.vue'

export default {
  name: 'AppSidebar',
  components: { BadgeNumeric, AppSidebarItem, SpeedDial, RriIcon },
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      route: useRoute()
    }
  },
  computed: {
    activeItemLink() {
      const matchedItem = this.items.find(item => this.isPathActive(item.link))
      const defaultItem = this.items.find(item => this.isDefaultProfileItem(item))
      return (matchedItem || defaultItem)?.link
    }
  },
  methods: {
    normalizePath(path = '') {
      return String(path)
        .split('?')[0]
        .split('#')[0]
        .replace(/^\/(en|de)(?=\/)/, '')
    },
    isDefaultProfileItem(item) {
      return item.icon === 'nav-user' || this.normalizePath(item.link) === '/profile'
    },
    isPathActive(targetPath) {
      const current = this.normalizePath(this.route.path)
      const target = this.normalizePath(targetPath)
      if (target === '/') return current === '/'
      return current === target || current.startsWith(target + '/')
    },
    isItemActive(item) {
      return Boolean(this.activeItemLink) && this.normalizePath(item.link) === this.normalizePath(this.activeItemLink)
    }
  }
}
</script>

<style scoped lang="scss">
.preview-sidebar {
  --preview-sidebar-fab-size: var(--space-56);
  --preview-sidebar-fab-background: var(--color-zinc-100);
  --preview-sidebar-fab-icon-color: var(--color-zinc-700);
  --preview-sidebar-fab-shadow: 0 4px 12px var(--color-opacity-black-15);

  position: fixed;
  top: 0;
  left: 0;
  width: 70px;
  height: 100vh;
  background-color: var(--color-static-blue-500);
  border-radius: 0 var(--border-radius-16) var(--border-radius-16) 0;
  padding: 150px 3px 0;
  z-index: 998;
  overflow: hidden;

  &__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-12);
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  &__fab {
    display: none;
  }

  @include tablet-down {
    width: unset;
    height: unset;
    top: unset;
    left: unset;
    bottom: 0;
    right: 0;
    background-color: transparent;
    border-radius: 0;
    padding: 0;
    overflow: visible;
    z-index: 1000;

    &__list {
      display: none;
    }

    &__fab {
      display: block;
      position: relative;
      // Sit above any fixed bottom controls bar (job controls, edit-form
      // save/cancel footer, …) whose height is published to this variable;
      // falls back to a plain 24px inset on pages without such a bar.
      bottom: calc(var(--bottom-controls-height, 0px) + var(--space-24));
      right: var(--space-24);
      z-index: 1001;

      :deep(.p-speeddial) {
        position: relative;
        align-items: flex-end;
      }

      :deep(.p-speeddial-list) {
        position: absolute;
        bottom: 100%;
        right: 0;
        align-items: flex-end;
        gap: var(--space-8);
        padding-bottom: var(--space-8);
      }

      :deep(.badge-numeric) {
        position: absolute;
        top: 6px;
        right: 6px;
        color: var(--color-white);
      }
    }
  }
}

.preview-sidebar__fab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--preview-sidebar-fab-size);
  height: var(--preview-sidebar-fab-size);
  border-radius: var(--border-radius-rounded);
  background-color: var(--color-static-blue-500);
  border: 1px solid transparent;
  cursor: pointer;
  box-shadow: var(--preview-sidebar-fab-shadow);
  --fill-0: var(--color-static-blue-50);
  transition: background-color 0.2s ease;

  &:hover,
  &--open {
    background-color: var(--preview-sidebar-fab-background);
    --fill-0: var(--preview-sidebar-fab-icon-color);
  }
}

.preview-sidebar__fab-btn-icon--open {
  transform: rotate(45deg);
}

.preview-sidebar__fab-item {
  position: relative;
  display: flex;
  align-items: center;

  &:hover .preview-sidebar__fab-label {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
}

.preview-sidebar__fab-label {
  position: absolute;
  right: calc(100% + var(--space-8));
  font-weight: var(--weight-medium);
  line-height: var(--leading-170);
  color: var(--color-zinc-50);
  background-color: var(--color-zinc-950);
  padding: var(--space-6) var(--space-12);
  border-radius: var(--border-radius-16);
  white-space: nowrap;
  opacity: 0;
  transform: translateX(8px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
}

.preview-sidebar__fab-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--preview-sidebar-fab-size);
  height: var(--preview-sidebar-fab-size);
  border-radius: var(--border-radius-rounded);
  background-color: var(--color-zinc-50);
  border: 1px solid var(--color-zinc-100);
  text-decoration: none;
  --fill-0: var(--preview-sidebar-fab-icon-color);
  transition: background-color 0.15s ease;
  box-shadow:
    0 100px 80px 0 rgba(0, 0, 0, 0.07),
    0 30.147px 24.118px 0 rgba(0, 0, 0, 0.05),
    0 12.522px 10.017px 0 rgba(0, 0, 0, 0.04),
    0 4.529px 3.623px 0 rgba(0, 0, 0, 0.02);

  &:active,
  &:hover {
    background-color: var(--preview-sidebar-fab-background);
  }

  &--active {
    background-color: var(--color-zinc-100);
    border-color: var(--color-zinc-200);
  }
}
</style>

<!--
  On tablet-down the preview sidebar collapses to a floating FAB (like mobile).
  Drop the layout's reserved left gutter wherever this v2 sidebar is rendered
  (job preview, profile preview, jobs list) so content spans full width.
  Keyed on the sidebar id so the regular (non-v2) TalentSidebar is unaffected.
-->
<style lang="scss">
@include tablet-down {
  // Override padding-left directly: --sidebar-width is set as an inline style on
  // .main, which a stylesheet custom-property rule cannot beat.
  .main:has(#app-sidebar) {
    padding-left: 0;
  }
}
</style>
